import { useState } from 'react';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      setFormStatus('idle');
      const response = await fetch(form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <div className="pt-24 pb-12 md:pb-16 min-h-screen">
      <div className="w-full max-w-5xl mx-auto px-4 md:px-6">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <span className="text-foreground-800 font-medium">关于我们</span>
        </nav>

        <section className="mb-14">
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">关于久久视频</h1>
          <p className="text-base text-foreground-600 leading-relaxed max-w-3xl">
            久久视频成立于2020年，是一家专注于在线视频内容聚合与分发的互联网平台。我们致力于通过技术驱动，为数亿用户提供海量高清视频内容的流畅观看体验。
          </p>
        </section>

        <section className="mb-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">品牌故事</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                久久视频的创始团队来自国内顶级互联网企业和视频内容行业，怀揣着"让每个人都能轻松享受优质视频内容"的初心，我们打造了久久视频这个平台。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                经过多年的技术积累和内容布局，久久视频已建立起覆盖影视剧、综艺节目、动漫番剧、纪录片、音乐MV、游戏赛事、知识科普等全方位的内容矩阵，日活跃用户超过5000万。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                我们始终坚持技术创新，自研的视频编解码和CDN分发技术确保了用户在任何网络环境下都能获得流畅的播放体验。同时，我们也在积极布局AI推荐算法，让每位用户都能发现属于自己的精彩内容。
              </p>
            </div>
            <div className="rounded-xl overflow-hidden h-64 lg:h-auto">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20warm%20lighting%2C%20team%20collaboration%20around%20large%20screen%20showing%20video%20content%2C%20plants%20and%20wooden%20furniture%2C%20cozy%20tech%20company%20atmosphere%2C%20natural%20light%20from%20large%20windows%2C%20professional%20yet%20relaxed%20environment&width=800&height=500&seq=about-brand-99&orientation=landscape"
                alt="久久视频团队"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        <section className="mb-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">用户口碑</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                name: '王先生',
                role: '影视爱好者',
                content: '久久视频的画质真的太棒了，4K超清播放配合家庭影院，观影体验完全不输电影院。资源也很全，想看的片子基本都有。',
                rating: 5,
              },
              {
                name: '李女士',
                role: '追剧达人',
                content: '每天都在久久视频上追剧，更新速度快，弹幕氛围也好。特别喜欢它的推荐算法，总能给我推送感兴趣的新剧。',
                rating: 5,
              },
              {
                name: '张同学',
                role: '大学生',
                content: '作为学生党，久久视频的免费内容真的很多。学习累了看会儿动漫或者纪录片，放松又涨知识，强烈推荐！',
                rating: 5,
              },
            ].map((review, idx) => (
              <div key={idx} className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: review.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  &ldquo;{review.content}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-sm font-bold text-primary-700">{review.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{review.name}</p>
                    <p className="text-xs text-foreground-400">{review.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">联系我们</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                  <i className="ri-mail-line text-lg text-primary-600"></i>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-foreground-900 mb-1">电子邮箱</h3>
                  <p className="text-sm text-foreground-500">contact@jiujiu-video.com</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                  <i className="ri-phone-line text-lg text-primary-600"></i>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-foreground-900 mb-1">客服电话</h3>
                  <p className="text-sm text-foreground-500">400-888-9999</p>
                  <p className="text-xs text-foreground-400">服务时间：周一至周日 9:00 - 21:00</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                  <i className="ri-map-pin-line text-lg text-primary-600"></i>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-foreground-900 mb-1">公司地址</h3>
                  <p className="text-sm text-foreground-500">北京市朝阳区光华路88号</p>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
              <h3 className="font-heading text-base font-bold text-foreground-900 mb-4">给我们留言</h3>
              {formStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-accent-600"></i>
                  </div>
                  <p className="text-foreground-800 font-medium">感谢您的留言！</p>
                  <p className="text-sm text-foreground-500 mt-1">我们会尽快回复您</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} action="https://readdy.ai/api/form/d9rdqvee3oq4jqumu04g" method="POST" data-readdy-form className="space-y-4">
                  <div>
                    <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-lg bg-white border border-background-200/70 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400 text-foreground-800"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-lg bg-white border border-background-200/70 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400 text-foreground-800"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1">留言内容</label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-3 py-2.5 text-sm rounded-lg bg-white border border-background-200/70 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400 text-foreground-800 resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  {formStatus === 'error' && (
                    <p className="text-sm text-primary-600">{formError}</p>
                  )}
                  <button
                    type="submit"
                    className="w-full py-2.5 text-sm font-medium rounded-lg bg-primary-500 hover:bg-primary-600 text-white transition-colors whitespace-nowrap cursor-pointer"
                  >
                    提交留言
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        <section>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">常见问题</h2>
          <div className="space-y-3">
            {[
              { q: '久久视频是否免费？', a: '久久视频提供大量免费高清内容，部分独家内容可能需要会员订阅。我们会持续丰富免费内容库，让更多用户享受优质视频服务。' },
              { q: '支持哪些设备观看？', a: '久久视频支持网页端、iOS App、Android App以及智能电视等多种设备，一个账号多端同步，随时随地畅享视频。' },
              { q: '如何下载久久视频APP？', a: '您可以在Apple App Store或各大安卓应用商店搜索"久久视频"下载官方APP。也可以通过官网底部链接直接下载。' },
              { q: '视频加载慢怎么办？', a: '建议检查您的网络连接。久久视频采用智能码率调节技术，会根据您的网络状况自动调整清晰度。如问题持续，请联系客服。' },
            ].map((faq, idx) => (
              <details key={idx} className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-200/50 transition-colors">
                  <span className="text-sm font-medium text-foreground-800">{faq.q}</span>
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </div>
              </details>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}